import { useDispatch, useSelector } from "react-redux";
import { useCallback, useState } from "react";
import {
  GAME_QUIZZES,
  CURRENT_GAME_QUIZ,
  LOADING_GAME_QUIZZES,
  LOADING_GAME_QUIZ_DETAIL,
  GAME_QUIZZES_PAGINATION,
} from "../store/selectors";
import {
  setLoading,
  setLoadingDetail,
  setGameQuizzesWithPagination,
  setCurrentGameQuiz,
  deleteGameQuiz as deleteGameQuizAction,
} from "../store/slices/gameQuiz";
import {
  getGameQuizzes,
  getGameQuizById as getGameQuizByIdService,
  deleteGameQuiz as deleteGameQuizService,
} from "../services/gameQuiz/gameQuiz.service";
import { SearchGameQuizParams } from "../services/gameQuiz/gameQuiz.types";
import { useNotification } from "./useNotification";

export const useGameQuizzes = () => {
  const dispatch = useDispatch();
  const gameQuizzes = useSelector(GAME_QUIZZES);
  const currentGameQuiz = useSelector(CURRENT_GAME_QUIZ);
  const loading = useSelector(LOADING_GAME_QUIZZES);
  const loadingDetail = useSelector(LOADING_GAME_QUIZ_DETAIL);
  const pagination = useSelector(GAME_QUIZZES_PAGINATION);
  const { notify } = useNotification();

  const [searchParams, setSearchParams] = useState<SearchGameQuizParams>({
    page: 1,
    pageSize: 10,
  });

  const fetchGameQuizzes = useCallback(
    async (params?: SearchGameQuizParams) => {
      dispatch(setLoading(true));
      try {
        const finalParams = { ...searchParams, ...params };
        const response = await getGameQuizzes(finalParams);
        dispatch(setGameQuizzesWithPagination(response));
      } catch (error) {
        dispatch(setLoading(false));
        notify(
          "error",
          "La información no pudo ser cargada, por favor notifica este error.",
          "Error",
        );
        console.error("Error fetching game quizzes:", error);
      }
    },
    [dispatch, searchParams, notify],
  );

  const updateSearchParams = useCallback(
    async (newParams: Partial<SearchGameQuizParams>) => {
      const updatedParams = { ...searchParams, ...newParams };
      if (newParams.search !== undefined || newParams.published !== undefined) {
        updatedParams.page = 1;
      }
      setSearchParams(updatedParams);
      try {
        await fetchGameQuizzes(updatedParams);
      } catch (error) {
        console.error("Error updating search params:", error);
      }
    },
    [searchParams, fetchGameQuizzes],
  );

  const changePage = useCallback(
    async (page: number, pageSize?: number) => {
      const newParams = { ...searchParams, page };
      if (pageSize) newParams.pageSize = pageSize;
      try {
        await updateSearchParams(newParams);
      } catch (error) {
        console.error("Error changing page:", error);
      }
    },
    [searchParams, updateSearchParams],
  );

  const refetch = async () => {
    await fetchGameQuizzes(searchParams);
  };

  const getGameQuizById = async (id: number) => {
    dispatch(setLoadingDetail(true));
    try {
      const response = await getGameQuizByIdService(id);
      dispatch(setCurrentGameQuiz(response));
      return response;
    } catch (error) {
      dispatch(setLoadingDetail(false));
      notify(
        "error",
        "La información no pudo ser cargada, por favor notifica este error.",
        "Error",
      );
      throw error;
    }
  };

  const deleteGameQuiz = async (id: number) => {
    dispatch(setLoading(true));
    try {
      const response = await deleteGameQuizService(id);
      if (response) {
        dispatch(deleteGameQuizAction(id));
        notify("success", "Quiz eliminado exitosamente.", "Éxito");
        try {
          await fetchGameQuizzes();
        } catch (error) {
          console.error("Error refetching game quizzes after delete:", error);
        }
      } else {
        notify("error", "El quiz no pudo ser eliminado.", "Error");
      }
      dispatch(setLoading(false));
    } catch {
      dispatch(setLoading(false));
      notify("error", "El quiz no pudo ser eliminado.", "Error");
    }
  };

  return {
    loading,
    loadingDetail,
    gameQuizzes,
    currentGameQuiz,
    pagination,
    refetch,
    getGameQuizById,
    deleteGameQuiz,
    updateSearchParams,
    changePage,
  };
};
